// What a role is offered (02-DESIGN/03-console/01-the-console.md § no
// backend: the console can do exactly what its placement's template admits
// and renders nothing as a permission; § the shape: the rail is derived from
// the role's offers, and what is listed is what the role may do). The role is
// read from the carrier the auth service minted and decides only what is
// offered; the broker decides what is admitted, and a refusal is shown as
// one. The rows below are the designs' tables: 01-tenants-and-roles.md § two
// human roles, 04-apps.md § the registration, 01-the-vault.md § access,
// 01-the-workload-model.md § the verbs, 04-placement-records.md § the console
// surface, 03-the-guardrails-service.md § templates and the import and § the
// console (the rule verbs an admin's, the escalation verbs everyone's),
// 01-the-toolkit-contract.md and 03-linking.md (discovery and a person's own
// linking for everyone; add and remove an admin's), and research 004's
// 03-the-shape.md § what each role sees.

import { identity } from "@impire-io/sdk";

import type { Page } from "../address/address.ts";

export type Role = identity.Role;

/** A verb the console offers, named by page. */
export type Verb =
  | "home"
  | "approvals:own"
  | "approvals:admin"
  | "toolkits:read"
  | "toolkits:link"
  | "toolkits:manage"
  | "guardrails"
  | "apps:register:personal"
  | "apps:register:tenant"
  | "apps:manage:own"
  | "apps:manage:any"
  | "apps:list:own"
  | "apps:list:any"
  | "workloads:register"
  | "workloads:list:own"
  | "workloads:list:any"
  | "workloads:manage:own"
  | "workloads:manage:any"
  | "workloads:suspend"
  | "solutions:read"
  | "solutions:manage"
  | "signins:own"
  | "audit"
  | "credentials:own"
  | "people:rows"
  | "people:members:add"
  | "people:kick"
  | "people:sign-out-all"
  | "workspaces"
  | "models:manage"
  | "configuration:own"
  | "configuration:any"
  | "stores:manage"
  | "secrets"
  | "settings";

// Workloads are an admin's page (hq decision 0047): a member is offered no
// verb that creates, changes or removes a workload, and keeps
// `workloads:list:own` only as the read Toolkits and Solutions make to find
// their workloads — it opens no page.
//
// The wave-two rows: 01-models-and-instances.md § the console (Models, an
// admin's), 01-the-configuration-service.md § who may do what (an admin on
// every workload's documents, a member on their own), and
// 01-stores-types-and-instances.md § who may do what (the store, type and
// index verbs are an admin's).
const MEMBER: readonly Verb[] = [
  "home",
  "approvals:own",
  "toolkits:read",
  "toolkits:link",
  "apps:register:personal",
  "apps:manage:own",
  "apps:list:own",
  "workloads:list:own",
  "configuration:own",
  "solutions:read",
  "signins:own",
  "credentials:own",
];

const ADMIN: readonly Verb[] = [
  ...MEMBER,
  "approvals:admin",
  "toolkits:manage",
  "guardrails",
  "apps:register:tenant",
  "apps:manage:any",
  "apps:list:any",
  "workloads:register",
  "workloads:manage:own",
  "workloads:list:any",
  "workloads:manage:any",
  "workloads:suspend",
  "solutions:manage",
  "audit",
  "people:rows",
  "people:members:add",
  "people:kick",
  "people:sign-out-all",
  "workspaces",
  "models:manage",
  "configuration:any",
  "stores:manage",
  "secrets",
  "settings",
];

const NONE: readonly Verb[] = [];

/** offers lists what a role is offered; a role the design does not name for a person is offered nothing. */
export function offers(role: Role | undefined): ReadonlySet<Verb> {
  switch (role) {
    case identity.Role.admin:
      return new Set(ADMIN);
    case identity.Role.member:
      return new Set(MEMBER);
    default:
      return new Set(NONE);
  }
}

/** offered reports whether a role is offered a verb. */
export function offered(role: Role | undefined, verb: Verb): boolean {
  return offers(role).has(verb);
}

/** An entry of the rail: Home, or one of the pages. */
export type RailEntry = Page | "home";

/** The verb that opens each entry: an entry is listed only where that verb is offered. */
const OPENS: Record<RailEntry, Verb> = {
  home: "home",
  approvals: "approvals:own",
  apps: "apps:list:own",
  toolkits: "toolkits:read",
  workloads: "workloads:list:any",
  solutions: "solutions:read",
  people: "people:rows",
  workspaces: "workspaces",
  guardrails: "guardrails",
  models: "models:manage",
  configuration: "configuration:any",
  stores: "stores:manage",
  secrets: "secrets",
  audit: "audit",
  settings: "settings",
  "me/activity": "signins:own",
  "me/credentials": "credentials:own",
};

/** The rail's three regions, in the design's order (§ the shape). */
const USE: readonly RailEntry[] = ["home", "approvals", "apps", "toolkits", "solutions"];
const ADMINISTRATION: readonly Page[] = [
  "people",
  "workspaces",
  "workloads",
  "models",
  "configuration",
  "guardrails",
  "stores",
  "secrets",
  "audit",
  "settings",
];
const PERSON: readonly Page[] = ["me/activity", "me/credentials"];

/** Rail is what a role's rail lists: what they use, what an admin sets, and the person's own pages. */
export interface Rail {
  use: RailEntry[];
  administration: Page[];
  person: Page[];
}

/** railFor derives the rail from the role's offers: a member's has no Administration. */
export function railFor(role: Role | undefined): Rail {
  const o = offers(role);
  return {
    use: USE.filter((e) => o.has(OPENS[e])),
    administration: ADMINISTRATION.filter((p) => o.has(OPENS[p])),
    person: PERSON.filter((p) => o.has(OPENS[p])),
  };
}

/** pageOffered reports whether a role's rail lists an entry. */
export function pageOffered(role: Role | undefined, entry: RailEntry): boolean {
  return offers(role).has(OPENS[entry]);
}

/**
 * manages reports whether a person may act on a thing with a registrant: an
 * admin on any, a member on their own (01-tenants-and-roles.md § two human
 * roles: "their own" is the registrant).
 */
export function manages(role: Role | undefined, me: string, registrant: string): boolean {
  if (role === identity.Role.admin) {
    return true;
  }
  if (role === identity.Role.member) {
    return me !== "" && me === registrant;
  }
  return false;
}
